import { initializeApp } from "firebase/app";
import {
  getRemoteConfig,
  fetchAndActivate,
  onConfigUpdate,
  activate,
  getValue,
} from "firebase/remote-config";

const app = initializeApp({
  apiKey: import.meta.env.VITE_APP_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_APP_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_FIREBASE_APP_ID,
});

const remoteConfig = getRemoteConfig(app);

remoteConfig.defaultConfig = {
  maintenanceMode: false,
};

function getMaintenanceMode(): boolean {
  if (
    typeof window !== "undefined" &&
    (window as Window & { Cypress?: unknown }).Cypress
  ) {
    try {
      const testToggles = JSON.parse(
        window.localStorage.getItem("__cypress.featureToggles") || "{}"
      );
      if (typeof testToggles["ENABLE_MAINTENANCE_MODE"] === "boolean") {
        return testToggles["ENABLE_MAINTENANCE_MODE"];
      }
    } catch {
      // ignore
    }
  }
  const value = getValue(remoteConfig, "maintenanceMode");
  const result = value.asBoolean();
  console.debug(
    "[RemoteConfig] maintenanceMode raw:",
    value.asString(),
    "| source:",
    value.getSource(),
    "| resolved:",
    result
  );
  return result;
}

export function subscribeToMaintenanceConfig(
  onChange: (maintenanceMode: boolean) => void
): () => void {
  if (
    typeof window !== "undefined" &&
    (window as Window & { Cypress?: unknown }).Cypress
  ) {
    onChange(getMaintenanceMode());
    return () => {};
  }

  let cancelled = false;
  let currentUnsubscribe: (() => void) | null = null;

  function connect() {
    if (cancelled) {
      return;
    }

    fetchAndActivate(remoteConfig)
      .then(() => {
        if (!cancelled) {
          onChange(getMaintenanceMode());
        }
      })
      .catch(() => {
        if (!cancelled) {
          onChange(false);
        }
      });

    const unsubscribe = onConfigUpdate(remoteConfig, {
      next: (configUpdate) => {
        console.debug(
          "[RemoteConfig] onConfigUpdate received, updatedKeys:",
          configUpdate.getUpdatedKeys()
        );
        activate(remoteConfig).then(() => {
          if (!cancelled) {
            onChange(getMaintenanceMode());
          }
        });
      },
      error: () => {
        if (!cancelled) {
          setTimeout(connect, 10_000);
        }
      },
      complete: () => {},
    });

    currentUnsubscribe = unsubscribe;
  }

  connect();

  return () => {
    cancelled = true;
    currentUnsubscribe?.();
  };
}
